import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Grid,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../../api/client';
import { ESTADO_COLORS, formatFecha, formatHora } from './agendaConstants';

const AgendaListado = ({ onEdit, onView, onCreate, refreshKey }) => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [filters, setFilters] = useState({});
  const [filterForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/agenda-reuniones', {
        params: { page, limit, ...filters },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar reuniones');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const applyFilters = () => {
    const v = filterForm.getFieldsValue();
    const next = {};
    if (v.fechaDesde) next.fechaDesde = v.fechaDesde;
    if (v.fechaHasta) next.fechaHasta = v.fechaHasta;
    if (v.cliente?.trim()) next.cliente = v.cliente.trim();
    if (v.contacto?.trim()) next.contacto = v.contacto.trim();
    if (v.producto?.trim()) next.producto = v.producto.trim();
    if (v.localidad?.trim()) next.localidad = v.localidad.trim();
    setPage(1);
    setFilters(next);
  };

  const clearFilters = () => {
    filterForm.resetFields();
    setPage(1);
    setFilters({});
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: 'Eliminar reunión',
      content: `¿Eliminar reunión del ${formatFecha(row.fecha)} con ${row.cliente}?`,
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/agenda-reuniones/${row.id}`);
          message.success('Reunión eliminada');
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      width: 100,
      render: (v) => formatFecha(v),
    },
    {
      title: 'Hora',
      key: 'hora',
      width: 90,
      render: (_, row) => formatHora(row.horaInicio),
    },
    { title: 'Cliente', dataIndex: 'cliente', ellipsis: true },
    { title: 'Contacto', dataIndex: 'contacto', ellipsis: true, responsive: ['md'] },
    { title: 'Teléfono', dataIndex: 'telefono', width: 120, responsive: ['lg'] },
    { title: 'Email', dataIndex: 'email', ellipsis: true, responsive: ['lg'] },
    { title: 'Producto', dataIndex: 'producto', ellipsis: true, responsive: ['md'] },
    { title: 'Localidad', dataIndex: 'localidad', ellipsis: true, responsive: ['lg'] },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 110,
      render: (v) => <Tag color={ESTADO_COLORS[v]}>{v}</Tag>,
    },
    {
      title: '',
      key: 'acciones',
      width: 110,
      fixed: isNarrow ? 'right' : undefined,
      render: (_, row) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onView(row)} />
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEdit(row)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Form form={filterForm} layout="inline" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Form.Item name="fechaDesde" label="Desde">
          <Input type="date" style={{ width: isNarrow ? '100%' : 150 }} />
        </Form.Item>
        <Form.Item name="fechaHasta" label="Hasta">
          <Input type="date" style={{ width: isNarrow ? '100%' : 150 }} />
        </Form.Item>
        <Form.Item name="cliente" label="Cliente">
          <Input allowClear placeholder="Cliente" style={{ width: isNarrow ? '100%' : 140 }} />
        </Form.Item>
        <Form.Item name="contacto" label="Contacto">
          <Input allowClear placeholder="Contacto" style={{ width: isNarrow ? '100%' : 120 }} />
        </Form.Item>
        <Form.Item name="producto" label="Producto">
          <Input allowClear placeholder="Producto" style={{ width: isNarrow ? '100%' : 120 }} />
        </Form.Item>
        <Form.Item name="localidad" label="Localidad">
          <Input allowClear placeholder="Localidad" style={{ width: isNarrow ? '100%' : 120 }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={applyFilters}>
              Filtrar
            </Button>
            <Button onClick={clearFilters}>Limpiar</Button>
            <Button icon={<PlusOutlined />} onClick={() => onCreate()}>
              Nueva
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} registros`,
          size: isNarrow ? 'small' : 'default',
          simple: isNarrow,
        }}
        scroll={{ x: isNarrow ? 520 : 1100 }}
        size="small"
      />
    </Space>
  );
};

export default AgendaListado;
